import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthCard } from '@/pages/auth/AuthCard';
import { PasswordInput } from '@/components/PasswordInput';
import { useRegisterMutation } from '@/services/api';
import { checkPasswordPolicy, isValidEmail } from '@/utils/auth';
import { parseApiError } from '@/utils/apiError';

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [duplicateBanner, setDuplicateBanner] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const emailValid = email === '' || isValidEmail(email);
  const pwPolicy = checkPasswordPolicy(password);
  const passwordsMatch = confirm === '' || password === confirm;

  const canSubmit =
    name.trim() !== '' &&
    isValidEmail(email) &&
    pwPolicy.ok &&
    confirm !== '' &&
    password === confirm &&
    !isLoading;

  function fieldError(field: string): string | null {
    return serverErrors[field] ?? null;
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setServerErrors({});
    setDuplicateBanner(false);
    try {
      await register({ name: name.trim(), email: email.trim(), password }).unwrap();
      navigate('/login', { replace: true, state: { banner: 'Account created — please sign in' } });
    } catch (err) {
      const parsed = parseApiError(err);
      if (parsed.code === 'CONFLICT') {
        setDuplicateBanner(true);
        setServerErrors({ email: 'An account already exists for this email.' });
      } else if (parsed.field) {
        setServerErrors({ [parsed.field]: parsed.message });
      } else {
        setServerErrors({ _form: parsed.message });
      }
    }
  }

  return (
    <AuthCard>
      {duplicateBanner && (
        <div className="banner error" role="alert">
          <span className="banner-icon">!</span>
          <span>
            An account already exists for this email. <Link to="/login">Sign in instead</Link>.
          </span>
        </div>
      )}
      {serverErrors['_form'] && (
        <div className="banner error" role="alert">
          <span className="banner-icon">!</span>
          <span>{serverErrors['_form']}</span>
        </div>
      )}
      <h1 className="title">Create your account</h1>
      <p className="subtitle">Sign up to get started.</p>
      <form onSubmit={onSubmit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor="su-name">
            Name
          </label>
          <input
            id="su-name"
            className="input"
            type="text"
            placeholder="Your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="su-email">
            Email
          </label>
          <input
            id="su-email"
            className={`input ${!emailValid || fieldError('email') ? 'invalid' : ''}`}
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {!emailValid && <div className="field-err">Invalid email format.</div>}
          {fieldError('email') && <div className="field-err">{fieldError('email')}</div>}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="su-password">
            Password
          </label>
          <PasswordInput
            allowAutofill
            id="su-password"
            invalid={password !== '' && !pwPolicy.ok}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={password}
            onChange={setPassword}
          />
          {password !== '' && !pwPolicy.ok ? (
            <div className="field-err">{pwPolicy.reason}</div>
          ) : (
            <div className="field-help">Minimum 12 characters, mixed character classes.</div>
          )}
        </div>
        <div className="field">
          <label className="field-label" htmlFor="su-confirm">
            Confirm password
          </label>
          <PasswordInput
            allowAutofill
            id="su-confirm"
            invalid={!passwordsMatch}
            autoComplete="new-password"
            placeholder="••••••••••••"
            value={confirm}
            onChange={setConfirm}
          />
          {!passwordsMatch && <div className="field-err">Passwords do not match.</div>}
        </div>
        <button className="btn-primary" type="submit" disabled={!canSubmit}>
          {isLoading ? 'Creating…' : 'Create account'}
        </button>
      </form>
      <p className="auth-foot">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthCard>
  );
}
